import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AuthCallback — processes the session_id from Emergent Auth URL fragment,
 * exchanges it for our JWT via /api/auth/google-session, then redirects to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const { loginWithGoogleSession } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState("");

    useEffect(() => {
        // useRef synchronous flag prevents double-run under StrictMode / rapid re-renders.
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match || !match[1]) {
            setError("Manglende session-id fra Google-login.");
            return;
        }
        const sessionId = decodeURIComponent(match[1]);

        (async () => {
            try {
                const { user, created } = await loginWithGoogleSession(sessionId);
                // Clear the fragment so a page refresh doesn't retry
                window.history.replaceState(null, "", window.location.pathname);
                toast.success(created ? `Velkommen, ${user.name || user.email}! Din konto er oprettet.` : `Velkommen tilbage, ${user.name || user.email}.`);
                navigate("/dashboard", { replace: true, state: { user } });
            } catch (e) {
                const msg = e?.response?.data?.detail || "Google-login mislykkedes. Prøv igen.";
                setError(msg);
                toast.error(msg);
                setTimeout(() => navigate("/login", { replace: true }), 2500);
            }
        })();
    }, [loginWithGoogleSession, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div data-testid="auth-callback-processing" className="flex flex-col items-center gap-4 max-w-md text-center px-6">
                {error ? (
                    <>
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold">!</div>
                        <p className="text-slate-800 font-semibold">{error}</p>
                        <p className="text-slate-500 text-sm">Sender dig tilbage til login...</p>
                    </>
                ) : (
                    <>
                        <Loader2 className="w-10 h-10 text-slate-600 animate-spin" />
                        <p className="text-slate-800 font-semibold">Logger dig ind med Google...</p>
                        <p className="text-slate-500 text-sm">Et øjeblik — vi opretter eller finder din konto.</p>
                    </>
                )}
            </div>
        </div>
    );
}
